import axios from "axios";
import Vue from "vue";
import VueAxios from "vue-axios";
import VueSignaturePad from "vue-signature-pad";
import Vuelidate from "vuelidate";
import App from "./App.vue";
import environment from "./environment";
import recipeHelpers from "@/helpers/helpers";
import router from "./router";
import store from "./store";
import vuetify from "./plugins/vuetify";
import * as Sentry from "@sentry/vue";
import { Integrations } from "@sentry/tracing";
import { baseApiUrl } from "./environment/values";
import { auth } from "./plugins/firebase";
import TabManager from "./services/tab-manager";
import { isBenignFirebaseSignOutError } from "./helpers/firebaseErrors";

import "./registerServiceWorker";
import "roboto-fontface/css/roboto/roboto-fontface.css";
import "@mdi/font/css/materialdesignicons.css";
import "@/helpers/filters";
import "./assets/css/main.css";

const PUBLIC_URL_PATHS = ["autologin/"];

function getRequestUrl(config) {
  const url = config.url || "";
  if (/^https?:\/\//i.test(url)) {
    return url;
  }
  return `${config.baseURL || ""}${url}`;
}

function isPublicRequest(config) {
  const requestUrl = getRequestUrl(config);
  return PUBLIC_URL_PATHS.some((path) => requestUrl.includes(path));
}

/* Axios */
export const axiosInstance = axios.create({
  baseURL: baseApiUrl,
  headers: {
    Authorization: {
      toString() {
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        const token = user?.stsTokenManager?.accessToken;

        if (user) {
          return `JWT ${token}`;
        }
        return "";
      },
    },
  },
  "Content-Type": "application/json",
});

export const eventBus = new Vue();

axiosInstance.interceptors.request.use(
  (config) => {
    config.metadata = { startTime: new Date() };

    if (isPublicRequest(config)) {
      delete config.headers.Authorization;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => {
    const endTime = new Date();
    const startTime = response.config.metadata.startTime;
    const timeTaken = endTime - startTime;
    console.log("Tiempo de respuesta:", timeTaken, "ms");

    const threshold = 2000;

    if (timeTaken > threshold) {
      eventBus.$emit("badConnection");
    } else {
      eventBus.$emit("goodConnection");
    }

    return response;
  },
  async (error) => {
    console.log("error: ", error);
    const errorCode = error.response ? Number(error.response.status) : null;

    if (errorCode === 401) {
      await store.dispatch("clearSession");
    }

    return Promise.reject(error);
  }
);

/* Sentry */
Sentry.init({
  Vue,
  dsn: process.env.VUE_APP_SENTRY_DSN,
  environment: process.env.VUE_APP_ENVIROMENT,
  release: `${process.env.VUE_APP_SENTRY_RELEASE}@${process.env.VUE_APP_VERSION}`,
  integrations: [
    new Integrations.BrowserTracing({
      routingInstrumentation: Sentry.vueRouterInstrumentation(router),
      tracingOrigins: ["localhost", /^\//],
    }),
    new Sentry.Replay({
      networkDetailAllowUrls: [
        window.location.origin,
        "https://backend.rapihogar.com.ar/",
        "https://backend.rapihogar.com.ar/v2/mobile/autologin/",
        "https://backend.rapihogar.com.ar/v2/mobile/request_technician/",
        "https://backend.rapihogar.com.ar/v2/mobile/notification/",
        "https://backend.rapihogar.com.ar/v2/mobile/notification_fcm/subscribe/",
      ],
      networkRequestHeaders: ["Cache-Control"],
      networkResponseHeaders: ["Referrer-Policy"],
    }),
  ],

  tracesSampleRate: 1.0,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});

const plugin = {
  install() {
    Vue.helpers = recipeHelpers;
    Vue.prototype.$helpers = recipeHelpers;
  },
};

Vue.use(Vuelidate);
Vue.use(VueAxios, axiosInstance);
Vue.use(VueSignaturePad);
Vue.use(plugin);

Vue.prototype.$environment = environment;

Vue.config.productionTip = false;

window.addEventListener("unhandledrejection", (event) => {
  if (isBenignFirebaseSignOutError(event.reason)) {
    event.preventDefault();
  }
});

let app;

TabManager.listen((event) => {
  if (event.type === "FORCE_LOGOUT") {
    store.dispatch("forceLogoutTab");
  }
});

const initializeApp = () => {
  if (!app) {
    app = new Vue({
      router,
      store,
      vuetify,
      render: (h) => h(App),
    }).$mount("#app");
  }
};

let initialized = false;

auth.onAuthStateChanged(async (user) => {
  await store.dispatch("handleAuthStateChanged", user);

  if (!initialized) {
    initialized = true;
    initializeApp();
  }
});
